import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

// Icons
const ShieldIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const XIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const ParentSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // URL params
  const token = searchParams.get('token');
  const childId = searchParams.get('child');

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invite, setInvite] = useState(null);
  const [childInfo, setChildInfo] = useState(null);
  const [step, setStep] = useState('loading'); // loading, login, signup, choose, success, error

  // Auth form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Trading permission choice
  const [allowTrading, setAllowTrading] = useState(true);

  // Validate token on mount
  useEffect(() => {
    const validateInvite = async () => {
      if (!token || !childId) {
        setError('Invalid invite link. Missing token or child ID.');
        setStep('error');
        setLoading(false);
        return;
      }

      try {
        // Check parent_invites table
        const { data: inviteData, error: inviteError } = await supabase
          .from('parent_invites')
          .select('*, users!parent_invites_child_id_fkey(id, first_name, username)')
          .eq('invite_token', token)
          .eq('child_id', childId)
          .single();

        if (inviteError || !inviteData) {
          // Fallback: check users table for older invites
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('id, first_name, username, parent_invite_token')
            .eq('id', childId)
            .eq('parent_invite_token', token)
            .single();

          if (userError || !userData) {
            setError('Invalid or expired invite link.');
            setStep('error');
            setLoading(false);
            return;
          }

          setChildInfo(userData);
        } else {
          // Check if expired
          if (new Date(inviteData.expires_at) < new Date()) {
            setError('This invite link has expired. Ask your child to send a new one.');
            setStep('error');
            setLoading(false);
            return;
          }

          // Check if already accepted
          if (inviteData.status === 'accepted') {
            setError('This invite has already been accepted.');
            setStep('error');
            setLoading(false);
            return;
          }

          setInvite(inviteData);
          setChildInfo(inviteData.users);
        }

        // If user is logged in, go to choose step
        if (user) {
          setStep('choose');
        } else {
          setStep('login');
        }
      } catch (err) {
        console.error('Error validating invite:', err);
        setError('Something went wrong. Please try again.');
        setStep('error');
      } finally {
        setLoading(false);
      }
    };

    validateInvite();
  }, [token, childId, user]);

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError(null);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) throw error;

      // Will redirect to choose step via useEffect
      setStep('choose');
    } catch (err) {
      setAuthError(err.message || 'Login failed');
    } finally {
      setAuthLoading(false);
    }
  };

  // Handle signup
  const handleSignup = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError(null);

    try {
      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
      });

      if (authError) throw authError;

      // Create user profile
      const { error: profileError } = await supabase
        .from('users')
        .insert({
          auth_id: authData.user.id,
          email: email.trim().toLowerCase(),
          first_name: firstName.trim(),
          last_name: lastName.trim() || null,
          is_parent: true,
          parent_will_trade: false, // They can enable later
          intake_completed: true,
        });

      if (profileError) throw profileError;

      setStep('choose');
    } catch (err) {
      setAuthError(err.message || 'Signup failed');
    } finally {
      setAuthLoading(false);
    }
  };

  // Handle approve/link child
  const handleApprove = async () => {
    setAuthLoading(true);
    setAuthError(null);

    try {
      // Get current user's profile
      const { data: parentProfile, error: parentError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      if (parentError || !parentProfile) {
        throw new Error('Could not find your profile');
      }

      // Create family link
      const { error: linkError } = await supabase
        .from('family_links')
        .upsert({
          parent_id: parentProfile.id,
          child_id: childId,
          can_trade: allowTrading,
        }, {
          onConflict: 'parent_id,child_id'
        });

      if (linkError) throw linkError;

      // Update invite status
      if (invite?.id) {
        await supabase
          .from('parent_invites')
          .update({
            status: 'accepted',
            accepted_at: new Date().toISOString(),
            parent_id: parentProfile.id,
          })
          .eq('id', invite.id);
      }

      // Mark parent as is_parent if not already
      await supabase
        .from('users')
        .update({ is_parent: true })
        .eq('id', parentProfile.id);

      setStep('success');
    } catch (err) {
      console.error('Error approving child:', err);
      setAuthError(err.message || 'Failed to link account');
    } finally {
      setAuthLoading(false);
    }
  };

  // Loading state
  if (loading || step === 'loading') {
    return (
      <div style={styles.container}>
        <div style={styles.loadingBox}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Validating invite...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (step === 'error') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>
            <XIcon />
          </div>
          <h1 style={styles.title}>Invalid Invite</h1>
          <p style={styles.errorText}>{error}</p>
          <button style={styles.button} onClick={() => navigate('/')}>
            Go to CardChase
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (step === 'success') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>
            <CheckIcon />
          </div>
          <h1 style={styles.title}>You're Connected!</h1>
          <p style={styles.subtitle}>
            {childInfo?.first_name || 'Your child'} is now linked to your account.
            {allowTrading && ' They can start trading with other collectors!'}
          </p>
          <button style={styles.button} onClick={() => navigate('/profile-picker')}>
            Go to Profile Picker
          </button>
        </div>
      </div>
    );
  }

  // Choose trading permission
  if (step === 'choose') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.iconBox}>
            <ShieldIcon />
          </div>
          <h1 style={styles.title}>Approve {childInfo?.first_name || 'Your Child'}</h1>
          <p style={styles.subtitle}>
            Set trading permissions for your child's CardChase account.
          </p>

          <div style={styles.optionsBox}>
            <button
              style={{
                ...styles.optionCard,
                borderColor: allowTrading ? '#22c55e' : '#2a2a2a',
                backgroundColor: allowTrading ? 'rgba(34, 197, 94, 0.1)' : '#1a1a1a',
              }}
              onClick={() => setAllowTrading(true)}
            >
              <div style={styles.optionHeader}>
                <div style={{
                  ...styles.radio,
                  borderColor: allowTrading ? '#22c55e' : '#4a4a4a',
                }}>
                  {allowTrading && <div style={styles.radioDot} />}
                </div>
                <span style={styles.optionTitle}>Allow Trading</span>
              </div>
              <p style={styles.optionDesc}>
                Your child can send/receive trade requests and chat with other traders.
              </p>
            </button>

            <button
              style={{
                ...styles.optionCard,
                borderColor: !allowTrading ? '#f59e0b' : '#2a2a2a',
                backgroundColor: !allowTrading ? 'rgba(245, 158, 11, 0.1)' : '#1a1a1a',
              }}
              onClick={() => setAllowTrading(false)}
            >
              <div style={styles.optionHeader}>
                <div style={{
                  ...styles.radio,
                  borderColor: !allowTrading ? '#f59e0b' : '#4a4a4a',
                }}>
                  {!allowTrading && <div style={{ ...styles.radioDot, backgroundColor: '#f59e0b' }} />}
                </div>
                <span style={styles.optionTitle}>View Only</span>
              </div>
              <p style={styles.optionDesc}>
                Your child can browse and collect cards, but cannot trade or chat.
              </p>
            </button>
          </div>

          {authError && <p style={styles.authError}>{authError}</p>}

          <button
            style={{ ...styles.button, opacity: authLoading ? 0.5 : 1 }}
            onClick={handleApprove}
            disabled={authLoading}
          >
            {authLoading ? 'Linking...' : 'Approve & Link Account'}
          </button>
        </div>
      </div>
    );
  }

  // Login/Signup form
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.iconBox}>
          <ShieldIcon />
        </div>
        <h1 style={styles.title}>
          {childInfo?.first_name || 'Your child'} wants to trade!
        </h1>
        <p style={styles.subtitle}>
          Sign in or create an account to manage their permissions.
        </p>

        {step === 'login' ? (
          <form onSubmit={handleLogin} style={styles.form}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              required
            />

            {authError && <p style={styles.authError}>{authError}</p>}

            <button
              type="submit"
              style={{ ...styles.button, opacity: authLoading ? 0.5 : 1 }}
              disabled={authLoading}
            >
              {authLoading ? 'Signing in...' : 'Sign In'}
            </button>

            <p style={styles.switchText}>
              Don't have an account?{' '}
              <button
                type="button"
                style={styles.linkButton}
                onClick={() => setStep('signup')}
              >
                Sign Up
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={handleSignup} style={styles.form}>
            <div style={styles.nameRow}>
              <input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={{ ...styles.input, flex: 1 }}
                required
              />
              <input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={{ ...styles.input, flex: 1 }}
              />
            </div>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              required
            />
            <input
              type="password"
              placeholder="Password (min 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              minLength={6}
              required
            />

            {authError && <p style={styles.authError}>{authError}</p>}

            <button
              type="submit"
              style={{ ...styles.button, opacity: authLoading ? 0.5 : 1 }}
              disabled={authLoading}
            >
              {authLoading ? 'Creating account...' : 'Create Account'}
            </button>

            <p style={styles.switchText}>
              Already have an account?{' '}
              <button
                type="button"
                style={styles.linkButton}
                onClick={() => setStep('login')}
              >
                Sign In
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #2a2a2a',
    borderTopColor: '#0ea5e9',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    color: '#6a6a6a',
    fontSize: '14px',
  },
  card: {
    backgroundColor: '#151515',
    borderRadius: '16px',
    border: '1px solid #2a2a2a',
    padding: '32px 24px',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
  },
  iconBox: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    backgroundColor: 'rgba(14, 165, 233, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#0ea5e9',
    margin: '0 auto 20px',
  },
  successIcon: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#22c55e',
    margin: '0 auto 20px',
  },
  errorIcon: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ef4444',
    margin: '0 auto 20px',
  },
  title: {
    color: '#ffffff',
    fontSize: '22px',
    fontWeight: '700',
    margin: '0 0 8px 0',
  },
  subtitle: {
    color: '#8a8a8a',
    fontSize: '14px',
    lineHeight: 1.5,
    margin: '0 0 24px 0',
  },
  errorText: {
    color: '#8a8a8a',
    fontSize: '14px',
    lineHeight: 1.5,
    margin: '0 0 24px 0',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  nameRow: {
    display: 'flex',
    gap: '12px',
  },
  input: {
    padding: '14px 16px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '15px',
    outline: 'none',
  },
  button: {
    padding: '14px 24px',
    backgroundColor: '#0ea5e9',
    border: 'none',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  authError: {
    color: '#ef4444',
    fontSize: '13px',
    margin: '4px 0 0 0',
    textAlign: 'left',
  },
  switchText: {
    color: '#6a6a6a',
    fontSize: '13px',
    marginTop: '16px',
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: '#0ea5e9',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: 0,
  },
  optionsBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '20px',
    textAlign: 'left',
  },
  optionCard: {
    width: '100%',
    padding: '16px',
    borderRadius: '12px',
    border: '2px solid',
    cursor: 'pointer',
    textAlign: 'left',
  },
  optionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '6px',
  },
  radio: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#22c55e',
  },
  optionTitle: {
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '600',
  },
  optionDesc: {
    color: '#6a6a6a',
    fontSize: '13px',
    margin: '0 0 0 32px',
    lineHeight: 1.4,
  },
};

export default ParentSignup;
